/**
 * The star on a lot — "put it in my list" (BXS-3627, M19).
 *
 * WHY IT IS A BUTTON AND NOT A LINK WHEN SIGNED OUT
 * -------------------------------------------------
 * The obvious build is a `routerLink` to `/buyer/auth/login` for guests and a
 * button for buyers. It cannot be: on the catalogue this sits INSIDE the card's
 * `<a>`, and an anchor inside an anchor is invalid HTML that browsers repair by
 * closing the outer one — which silently breaks the card's own link on every
 * card. So it is always a `<button>`, and a signed-out press navigates in
 * TypeScript.
 *
 * WHY IT SHOWS AT ALL WHEN SIGNED OUT
 * -----------------------------------
 * Saeed's complaint was that the feature is not there — *"ما في"*. Hiding the
 * control from guests reproduces exactly that experience for anyone who has not
 * signed in yet, and they are the majority on a public partner catalogue. So
 * the star is always visible, and pressing it while signed out is the
 * invitation: it goes to the login page rather than doing nothing.
 *
 * WHY IT SWALLOWS THE CLICK
 * -------------------------
 * On the catalogue this is inside the card's link, so a press must
 * `preventDefault` and `stopPropagation` or starring a lot also navigates to
 * it. That is not a defensive nicety; without it the feature is unusable on the
 * surface it was asked for.
 */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';

import { LanguageNavigationService } from 'src/app/Core/locale/language-navigation.service';
import { InternationalMyListService } from '../../../../_services/international-my-list.service';

@Component({
  selector: 'app-international-favourite-button',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './international-favourite-button.component.html',
  styleUrl: './international-favourite-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternationalFavouriteButtonComponent implements OnInit, OnDestroy {
  /** The lot this star belongs to. */
  @Input({ required: true }) lotId!: number;

  /** `card` on the catalogue grid, `bar` on the lot profile's action row. */
  @Input() variant: 'card' | 'bar' | 'row' = 'card';

  /** Shows the word beside the star. Off on a card, where space is the enemy. */
  @Input() showLabel = false;

  /** Raised after a successful change, for a parent that keeps its own list. */
  @Output() changed = new EventEmitter<boolean>();

  isFavourite = false;
  hasNote = false;
  busy = false;

  private sub?: Subscription;

  constructor(
    private readonly myList: InternationalMyListService,
    private readonly router: Router,
    private readonly languageNavigation: LanguageNavigationService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // One subscription to the shared map rather than an @Input from every
    // parent: the same lot can be on screen twice (a card and the lot profile
    // behind a dialog), and both must agree the instant either changes.
    this.sub = this.myList.states$.subscribe((states) => {
      const state = states.get(this.lotId);
      this.isFavourite = state?.is_favourite ?? false;
      this.hasNote = state?.has_note ?? false;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get signedIn(): boolean {
    return this.myList.isEnabled;
  }

  /** The tooltip and the screen-reader name, which are the same sentence. */
  get labelKey(): string {
    if (!this.signedIn) {
      return 'international_auctions.my_list.sign_in_to_save';
    }
    return this.isFavourite
      ? 'international_auctions.my_list.remove'
      : 'international_auctions.my_list.add';
  }

  onPress(event: Event): void {
    // See the header: this lives inside the card's anchor on the catalogue.
    event.preventDefault();
    event.stopPropagation();

    if (this.busy) {
      return;
    }

    if (!this.signedIn) {
      this.router.navigateByUrl(
        this.languageNavigation.toLocalizedUrl('/buyer/auth/login')
      );
      return;
    }

    // The state the buyer is ASKING for, not a toggle of what we last saw.
    // Two fast presses then resolve to the same answer instead of racing.
    const wanted = !this.isFavourite;

    this.busy = true;
    this.cdr.markForCheck();

    this.myList.setFavourite(this.lotId, wanted).subscribe({
      next: (result) => {
        this.busy = false;
        if (result.ok) {
          this.changed.emit(wanted);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.busy = false;
        this.cdr.markForCheck();
      },
    });
  }
}
